import React, { useEffect, useState, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import {
  ArrowLeft, Check, Sparkles, ShieldCheck, ArrowRight, Info, Ticket, Tag, CheckCircle2, X,
} from 'lucide-react';
import BrandMark from '../components/BrandMark';
import PaymentsDownNotice from '../components/PaymentsDownNotice';

/**
 * Pricing page — reads tiers live from /api/subscription-tiers.
 * Beautiful, simple: monthly/annual toggle, 3 tier cards, comparison of key perks.
 */

const Pricing = () => {
  const { API, user, refreshUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromSignup = searchParams.get('from') === 'signup';
  const [redeemResult, setRedeemResult] = useState(null); // { tier, days, expires_at } after redeem — drives success modal
  const [saveCardLoading, setSaveCardLoading] = useState(false);
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [billing, setBilling] = useState('annual'); // 'monthly' | 'annual'
  const [subscribingId, setSubscribingId] = useState(null);
  // Global payments kill-switch — swaps every "Get started" CTA for the
  // notify-me notice while /api/payments/status.enabled is false.
  const [paymentsEnabled, setPaymentsEnabled] = useState(true);
  const [paymentsMsg, setPaymentsMsg] = useState('');
  useEffect(() => {
    axios.get(`${API}/payments/status`).then(({ data }) => {
      setPaymentsEnabled(!!data?.enabled);
      setPaymentsMsg(data?.message || '');
    }).catch(() => { /* default to enabled on probe failure */ });
  }, [API]);
  // Which tier the user just clicked — opens the "Review & apply code" step
  // before Stripe so they always get a chance to enter/edit the promo code.
  const [pendingTier, setPendingTier] = useState(null);
  // Promo state — influencer code lives in localStorage so /checkout/session
  // can read it later. Access code is redeemed inline (no Stripe).
  const [accessCode, setAccessCode] = useState('');
  const [redeemingCode, setRedeemingCode] = useState(false);
  const [influencerCode, setInfluencerCode] = useState(() => {
    try { return localStorage.getItem('fda_influencer_code') || ''; } catch { return ''; }
  });
  const [influencerPreview, setInfluencerPreview] = useState(null);
  // Debounced influencer-code validation so the tier prices can preview the
  // discount before the user even clicks "Get started".
  useEffect(() => {
    const code = (influencerCode || '').trim().toUpperCase();
    if (!code) { setInfluencerPreview(null); return; }
    const h = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/influencer/codes/${encodeURIComponent(code)}/validate`);
        setInfluencerPreview(data);
        if (data?.valid) {
          try { localStorage.setItem('fda_influencer_code', code); } catch { /* ignore */ }
        }
      } catch { setInfluencerPreview({ valid: false }); }
    }, 400);
    return () => clearTimeout(h);
  }, [influencerCode, API]);

  const clearInfluencer = () => {
    setInfluencerCode('');
    setInfluencerPreview(null);
    try { localStorage.removeItem('fda_influencer_code'); } catch { /* ignore */ }
  };

  // Referral code — retro-attach an inviter's personal code (e.g. TEST-P6ZJ)
  // to an already-signed-up account. One-shot; refuses self-refer / duplicate.
  const [referralCode, setReferralCode] = useState('');
  const [attachingReferral, setAttachingReferral] = useState(false);
  const [referralAttached, setReferralAttached] = useState(null); // { referrer_name }
  const handleAttachReferral = async () => {
    const code = (referralCode || '').trim().toUpperCase();
    if (!code) { toast.error('Enter a referral code'); return; }
    if (!user) { navigate('/login'); return; }
    setAttachingReferral(true);
    try {
      const tk = window.localStorage.getItem('token');
      const { data } = await axios.post(
        `${API}/referrals/attach`,
        { code },
        { headers: tk ? { Authorization: `Bearer ${tk}` } : {} },
      );
      setReferralAttached({ referrer_name: data.referrer_name || 'A member' });
      setReferralCode('');
      toast.success(`Thanks! ${data.referrer_name || 'Your inviter'} just got credited for referring you.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Referral code could not be attached');
    } finally {
      setAttachingReferral(false);
    }
  };

  // Pre-populate the referral input from ?ref= or the localStorage crumb the
  // landing page dropped when the user clicked a personal referral link.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const q = (params.get('ref') || '').trim();
      const stored = (window.localStorage.getItem('fda_ref_code') || '').trim();
      const pick = q || stored;
      if (pick && !/^(FDA|FDACADEMY)-/i.test(pick)) {
        setReferralCode(pick.toUpperCase());
      }
    } catch (_) { /* ignore */ }
  }, []);

  const handleRedeemAccessCode = async () => {
    const code = accessCode.trim().toUpperCase();
    if (!code) { toast.error('Enter an access code'); return; }
    if (!user) { navigate('/login'); return; }
    setRedeemingCode(true);
    try {
      const tk = window.localStorage.getItem('token');
      const { data } = await axios.post(
        `${API}/access-codes/redeem`,
        { code },
        { headers: tk ? { Authorization: `Bearer ${tk}` } : {} },
      );
      toast.success(`Code redeemed — ${(data.tier || '').toUpperCase()} unlocked for ${data.days || ''} days!`);
      setAccessCode('');
      setPendingTier(null);
      // Re-fetch the user so role/access_expires_at update everywhere (nav
      // badges, dashboard tier stat, banner countdown) without a hard reload.
      if (refreshUser) await refreshUser();
      // Show the celebration + upsell modal instead of navigating away —
      // the user can save a card for annual renewal (with 20% off) or
      // dismiss and land on their dashboard.
      setRedeemResult({
        tier: data.tier,
        days: data.days,
        expires_at: data.expires_at,
        product_name: data.product_name,
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Code could not be redeemed');
    } finally {
      setRedeemingCode(false);
    }
  };
  // Currency toggle — Pricing is stored in USD backend-side. CAD is a client-only
  // display conversion at a fixed rate (updated ~monthly). We persist the user's
  // choice so the same visitor lands on their currency next time.
  const CAD_PER_USD = 1.37;
  const [currency, setCurrency] = useState(() => {
    try { return localStorage.getItem('fda_currency') || 'USD'; } catch { return 'USD'; }
  });
  const setCurrencyAndRemember = (c) => {
    setCurrency(c);
    try { localStorage.setItem('fda_currency', c); } catch { /* ignore */ }
  };
  const toDisplay = (usd) => currency === 'CAD' ? usd * CAD_PER_USD : usd;
  const currencyLabel = currency === 'CAD' ? 'CAD' : 'USD';

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/subscription-tiers`);
        const list = (res.data || []).filter(t => t.enabled).sort((a, b) => a.order - b.order);
        setTiers(list);
      } catch (e) {
        toast.error('Could not load pricing');
      } finally {
        setLoading(false);
      }
    })();
  }, [API]);

  const currentRole = user?.role;

  const handleSubscribe = (tier) => {
    if (!user) { navigate('/login'); return; }
    // Instead of jumping straight to Stripe, open the review modal so the
    // user can add / edit / clear a promo code right before paying.
    setPendingTier(tier);
  };

  const confirmCheckout = async () => {
    const tier = pendingTier;
    if (!tier || !user) return;
    setSubscribingId(tier.id);
    try {
      let inf_code = null;
      try { inf_code = window.localStorage.getItem('fda_influencer_code') || null; } catch { /* ignore */ }
      const payload = {
        items: [{ kind: 'subscription', ref_id: tier.id, quantity: 1, meta: { billing_cycle: billing } }],
        origin_url: window.location.origin,
        influencer_code: inf_code,
        metadata: { tier_name: tier.name, billing_cycle: billing },
      };
      const headers = {};
      const tk = window.localStorage.getItem('token');
      if (tk) headers.Authorization = `Bearer ${tk}`;
      const res = await axios.post(`${API}/checkout/session`, payload, { headers });
      if (res.data?.url) {
        const disc = res.data?.discount;
        if (disc && disc.discount_pct > 0) {
          toast.success(
            `Applying ${disc.code} (${disc.discount_pct}% off) — you'll be charged $${(res.data.amount_cents / 100).toFixed(2)} instead of $${(res.data.original_amount_cents / 100).toFixed(2)}.`,
            { duration: 5000 },
          );
        }
        window.location.href = res.data.url;
      } else {
        toast.error('Could not start checkout');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start subscription');
    } finally {
      setSubscribingId(null);
    }
  };

  const price = (t) => (billing === 'annual' ? t.annual_price / 12 : t.monthly_price);
  const savings = (t) => {
    if (!t.monthly_price || !t.annual_price) return null;
    const yearlyIfMonthly = t.monthly_price * 12;
    const pct = Math.round(((yearlyIfMonthly - t.annual_price) / yearlyIfMonthly) * 100);
    return pct > 0 ? pct : null;
  };

  // Live-discounted display price when a valid influencer code is applied.
  const applyDiscount = (usd) => {
    const pct = influencerPreview?.valid ? Number(influencerPreview.discount_pct || 0) : 0;
    if (!pct) return usd;
    return usd * (1 - pct / 100);
  };
  const activeDiscountPct = influencerPreview?.valid ? Number(influencerPreview.discount_pct || 0) : 0;

  return (
    <div className="min-h-screen bg-background" data-testid="pricing-page">
      {redeemResult && (
        <RedeemSuccessModal
          result={redeemResult}
          tiers={tiers}
          onSaveCard={async () => {
            setSaveCardLoading(true);
            try {
              // Match Pro tier to the just-granted role; fall back to first
              // paid tier if role name doesn't match a tier row.
              const role = (redeemResult.tier || '').toLowerCase();
              const tier = tiers.find((t) => (t.name || '').toLowerCase() === role) || tiers[0];
              if (!tier) { navigate('/dashboard'); return; }
              const tk = window.localStorage.getItem('token');
              const headers = tk ? { Authorization: `Bearer ${tk}` } : {};
              try { localStorage.setItem('fda_influencer_code', 'FREETRIAL20'); } catch { /* ignore */ }
              const { data } = await axios.post(
                `${API}/checkout/session`,
                {
                  items: [{ kind: 'subscription', ref_id: tier.id, quantity: 1, meta: { billing_cycle: 'annual' } }],
                  origin_url: window.location.origin,
                  influencer_code: 'FREETRIAL20',
                  metadata: { source: 'redeem_success_upsell' },
                },
                { headers },
              );
              if (data?.url) {
                window.location.href = data.url;
              } else {
                toast.error('Could not start checkout');
              }
            } catch (e) {
              toast.error(e.response?.data?.detail || 'Could not open Stripe');
            } finally {
              setSaveCardLoading(false);
            }
          }}
          onDismiss={() => {
            setRedeemResult(null);
            navigate('/dashboard');
          }}
          saveCardLoading={saveCardLoading}
        />
      )}
      {pendingTier && (
        <ReviewCheckoutModal
          tier={pendingTier}
          billing={billing}
          currencyLabel={currencyLabel}
          toDisplay={toDisplay}
          basePrice={price(pendingTier)}
          yearlyTotal={billing === 'annual' ? pendingTier.annual_price : pendingTier.monthly_price}
          influencerCode={influencerCode}
          setInfluencerCode={setInfluencerCode}
          influencerPreview={influencerPreview}
          clearInfluencer={clearInfluencer}
          accessCode={accessCode}
          setAccessCode={setAccessCode}
          onRedeemAccessCode={handleRedeemAccessCode}
          redeemingCode={redeemingCode}
          onClose={() => setPendingTier(null)}
          onContinue={confirmCheckout}
          continuing={subscribingId === pendingTier.id}
        />
      )}
      {/* Top nav */}
      <header className="sticky top-0 z-30 bg-background/95 backdrop-blur border-b border-border">
        <div className="container mx-auto px-6 h-16 flex items-center justify-between">
          <Button
            variant="ghost" size="sm"
            className="-ml-3 font-semibold"
            onClick={() => navigate(user ? '/dashboard' : '/')}
            data-testid="pricing-back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
          <BrandMark size="md" onClick={() => navigate(user ? '/dashboard' : '/')} />
          <div className="w-[80px]" />
        </div>
      </header>

      {/* Payments-down banner — shown while /api/payments/status.enabled
          is false. Access-code redemption still works below, so the
          90-day Pro trial path is unaffected. */}
      {!paymentsEnabled && (
        <div className="container mx-auto px-6 pt-4">
          <PaymentsDownNotice context="subscription" message={paymentsMsg} />
        </div>
      )}

      {/* Hero */}
      <section className="bg-navy-spotlight text-background">
        <div className="container mx-auto px-6 pt-14 pb-16 text-center">
          <span className="pill-badge">Membership</span>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mt-6 text-balance">
            Simple pricing.
            <br />
            <span className="text-amber-brand">Pick what fits your operation.</span>
          </h1>
          <p className="mt-5 max-w-xl mx-auto text-background/70 leading-relaxed">
            No trial gimmicks. Free members get real tools. Paid tiers add the calculators, AI and
            community features serious operators rely on.
          </p>

          {/* Billing + Currency toggles */}
          <div className="mt-9 flex items-center justify-center gap-3 flex-wrap">
            <div className="inline-flex items-center gap-1 bg-background/10 border border-background/15 rounded-full p-1" role="tablist" data-testid="billing-toggle">
              <button
                onClick={() => setBilling('monthly')}
                className={`px-5 py-2 rounded-full text-sm font-semibold transition ${
                  billing === 'monthly' ? 'bg-background text-foreground' : 'text-background/70 hover:text-background'
                }`}
                data-testid="billing-monthly-btn"
              >
                Monthly
              </button>
              <button
                onClick={() => setBilling('annual')}
                className={`px-5 py-2 rounded-full text-sm font-semibold transition inline-flex items-center gap-2 ${
                  billing === 'annual' ? 'bg-background text-foreground' : 'text-background/70 hover:text-background'
                }`}
                data-testid="billing-annual-btn"
              >
                Annual
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-primary text-primary-foreground font-bold">Save ~17%</span>
              </button>
            </div>
            <div className="inline-flex items-center gap-1 bg-background/10 border border-background/15 rounded-full p-1" role="tablist" data-testid="currency-toggle">
              <button
                onClick={() => setCurrencyAndRemember('USD')}
                className={`px-4 py-2 rounded-full text-xs font-bold transition ${
                  currency === 'USD' ? 'bg-background text-foreground' : 'text-background/70 hover:text-background'
                }`}
                data-testid="currency-usd-btn"
                aria-label="Show prices in United States Dollars"
              >
                USD
              </button>
              <button
                onClick={() => setCurrencyAndRemember('CAD')}
                className={`px-4 py-2 rounded-full text-xs font-bold transition ${
                  currency === 'CAD' ? 'bg-background text-foreground' : 'text-background/70 hover:text-background'
                }`}
                data-testid="currency-cad-btn"
                aria-label="Show prices in Canadian Dollars"
              >
                CAD
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Tier cards */}
      <main className="container mx-auto px-6 py-14">

        {/* Signup welcome banner — only shown right after account creation. */}
        {fromSignup && (
          <div className="max-w-3xl mx-auto mb-6 rounded-2xl border border-primary/30 bg-primary/5 p-5 flex items-start gap-3" data-testid="pricing-signup-welcome">
            <CheckCircle2 className="w-5 h-5 text-primary shrink-0 mt-0.5" />
            <div>
              <div className="font-heading text-lg font-bold">Welcome — pick how you want to start.</div>
              <p className="text-sm text-muted-foreground mt-1">
                Stay on <b>Free</b>, redeem an <b>access code</b> for a free upgrade, or subscribe with an
                optional <b>creator code</b> for a discount. You can change plans anytime.
              </p>
            </div>
          </div>
        )}

        {/* Free-to-start + code-redemption band (payment gateway front door) */}
        <div className="max-w-4xl mx-auto mb-10 grid md:grid-cols-2 gap-4" data-testid="pricing-gateway">
          {/* Free path */}
          <div className="pro-card p-6 flex flex-col" data-testid="free-start-card">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-primary" />
              <div className="text-[10px] uppercase tracking-widest font-bold text-primary">Free to start</div>
            </div>
            <h3 className="font-heading text-2xl font-extrabold tracking-tight mt-2">Get in for $0.</h3>
            <p className="text-sm text-muted-foreground mt-1 flex-1">
              Explore the product database, calculators and community. Upgrade whenever you're ready.
            </p>
            <Button
              size="lg"
              variant="outline"
              className="mt-4 h-11 rounded-full font-bold w-full"
              onClick={() => navigate(user ? '/dashboard' : '/login?mode=register')}
              data-testid="continue-free-btn"
            >
              {user ? 'Continue on Free' : 'Create free account'} <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>

          {/* Code redemption */}
          <div className="pro-card p-6" data-testid="promo-codes-card">
            <div className="flex items-center gap-2">
              <Ticket className="w-4 h-4 text-primary" />
              <div className="text-[10px] uppercase tracking-widest font-bold text-primary">Have a code?</div>
            </div>
            <h3 className="font-heading text-lg font-extrabold tracking-tight mt-2">Redeem before you upgrade.</h3>

            {/* Access code — instant tier grant, no Stripe */}
            <div className="mt-3">
              <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">
                Access code · unlock a tier free
              </label>
              <div className="flex gap-2 mt-1.5">
                <Input
                  value={accessCode}
                  onChange={(e) => setAccessCode(e.target.value.toUpperCase())}
                  placeholder="XX-XXXX-XXXX-XXXX"
                  className="h-10 font-mono uppercase tracking-widest"
                  data-testid="access-code-input"
                />
                <Button
                  size="sm"
                  onClick={handleRedeemAccessCode}
                  disabled={redeemingCode || !accessCode.trim()}
                  className="h-10 rounded-full font-bold px-4"
                  data-testid="redeem-access-code-btn"
                >
                  {redeemingCode ? 'Redeeming…' : 'Redeem'}
                </Button>
              </div>
              <p className="text-[10px] text-muted-foreground mt-1">Single-use. Instantly upgrades your account — no payment.</p>
            </div>

            {/* Influencer code — applies discount to Stripe checkout */}
            <div className="mt-4 pt-4 border-t border-border">
              <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">
                Creator / influencer code · applies at checkout
              </label>
              <div className="flex gap-2 mt-1.5">
                <Input
                  value={influencerCode}
                  onChange={(e) => setInfluencerCode(e.target.value.toUpperCase())}
                  placeholder="LAUNCH25"
                  className="h-10 font-mono uppercase tracking-widest"
                  data-testid="influencer-code-input"
                />
                {influencerCode && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={clearInfluencer}
                    className="h-10 rounded-full px-3"
                    data-testid="clear-influencer-btn"
                  >
                    Clear
                  </Button>
                )}
              </div>
              {influencerPreview && influencerCode && (
                influencerPreview.valid ? (
                  <div
                    className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 text-emerald-800 px-2.5 py-1 text-[11px] font-bold mt-2"
                    data-testid="influencer-preview-valid"
                  >
                    <CheckCircle2 className="w-3 h-3" />
                    {influencerPreview.discount_pct > 0
                      ? `${influencerPreview.discount_pct}% off — auto-applied on the tier below`
                      : 'Creator code recognised · attribution active'}
                  </div>
                ) : (
                  <div
                    className="inline-flex items-center gap-1.5 rounded-full bg-red-100 text-red-800 px-2.5 py-1 text-[11px] font-bold mt-2"
                    data-testid="influencer-preview-invalid"
                  >
                    Code not found or inactive
                  </div>
                )
              )}
            </div>

            {/* Referral code — retro-attach an inviter (one-shot) */}
            <div className="mt-4 pt-4 border-t border-border" data-testid="referral-code-section">
              <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">
                Referral code · credit whoever invited you
              </label>
              {referralAttached ? (
                <div
                  className="mt-2 rounded-lg border border-emerald-500/30 bg-emerald-50 dark:bg-emerald-500/10 p-3 text-[12px]"
                  data-testid="referral-attached-success"
                >
                  <div className="inline-flex items-center gap-1.5 font-bold text-emerald-800 dark:text-emerald-300">
                    <CheckCircle2 className="w-3.5 h-3.5" /> Referral saved
                  </div>
                  <p className="text-muted-foreground mt-1">
                    Thanks — <b>{referralAttached.referrer_name}</b> has been credited for referring you.
                  </p>
                </div>
              ) : (
                <>
                  <div className="flex gap-2 mt-1.5">
                    <Input
                      value={referralCode}
                      onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                      placeholder="FRIEND-1234"
                      className="h-10 font-mono uppercase tracking-widest"
                      data-testid="referral-code-input"
                    />
                    <Button
                      size="sm"
                      onClick={handleAttachReferral}
                      disabled={attachingReferral || !referralCode.trim()}
                      className="h-10 rounded-full font-bold px-4"
                      data-testid="attach-referral-code-btn"
                    >
                      {attachingReferral ? 'Saving…' : 'Apply'}
                    </Button>
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-1">
                    One-time use. Credits the person who invited you — no discount on your plan.
                  </p>
                </>
              )}
            </div>
          </div>
        </div>

        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="pro-card p-7 animate-pulse">
                <div className="h-4 w-20 bg-muted rounded mb-4" />
                <div className="h-10 w-32 bg-muted rounded mb-4" />
                <div className="space-y-2">
                  {[1,2,3,4].map(k => <div key={k} className="h-3 bg-muted rounded" />)}
                </div>
                <div className="h-11 w-full bg-muted rounded-full mt-6" />
              </div>
            ))}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 items-stretch">
            {/* Free tier — always first */}
            <TierCard
              tier={{
                id: 'free',
                display_name: 'Free',
                description: 'For hobbyists getting started.',
                features: [
                  '10 Academy AI credits / month',
                  'Freeze-dry product database',
                  'Basic household calculator',
                  'Community forum access (read)',
                  'Vendor directory browsing',
                ],
              }}
              billing={billing}
              priceOverride={0}
              current={currentRole === 'free' || !currentRole}
              onSubscribe={() => (user ? navigate('/dashboard') : navigate('/login'))}
              ctaLabel={user ? 'Current plan' : 'Get started'}
              disabled={currentRole === 'free' && !!user}
            />

            {tiers.map((t) => (
              <TierCard
                key={t.id}
                tier={t}
                billing={billing}
                priceOverride={toDisplay(applyDiscount(price(t)))}
                originalPrice={activeDiscountPct > 0 ? toDisplay(price(t)) : null}
                discountPct={activeDiscountPct}
                yearlyTotal={billing === 'annual' ? toDisplay(applyDiscount(t.annual_price)) : null}
                currencyLabel={currencyLabel}
                savings={billing === 'annual' ? savings(t) : null}
                popular={t.popular}
                current={currentRole === t.name?.toLowerCase()}
                onSubscribe={() => handleSubscribe(t)}
                loading={subscribingId === t.id}
                ctaLabel={
                  currentRole === t.name?.toLowerCase()
                    ? 'Current plan'
                    : (user ? `Upgrade to ${t.display_name}` : `Get ${t.display_name}`)
                }
                disabled={currentRole === t.name?.toLowerCase()}
              />
            ))}
          </div>
        )}

        {/* AI Credits explainer — tier allowances + cost table */}
        <div className="mt-14 max-w-5xl mx-auto" data-testid="ai-credits-section">
          <div className="text-center mb-8">
            <span className="pill-badge"><Sparkles className="w-3 h-3" /> AI Credits</span>
            <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight mt-4">
              Predictable AI, priced by the drop.
            </h2>
            <p className="mt-3 text-muted-foreground max-w-2xl mx-auto">
              Every AI tool inside Freeze Dry Academy costs a small number of <b>credits</b>. Your monthly
              allowance is granted on the 1st and resets each month — no rollover, no surprises.
            </p>
          </div>

          {/* Monthly allowance grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
            {[
              { label: 'Free',        credits: 10,    color: 'from-slate-400 to-slate-500' },
              { label: 'Professional', credits: 100,  color: 'from-amber-brand to-amber-600' },
              { label: 'Business',    credits: 300,   color: 'from-emerald-500 to-emerald-600' },
              { label: 'Enterprise',  credits: 1000,  color: 'from-indigo-500 to-indigo-600' },
            ].map((t) => (
              <div
                key={t.label}
                className="pro-card p-5 text-center relative overflow-hidden"
                data-testid={`ai-tier-allowance-${t.label.toLowerCase()}`}
              >
                <div className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${t.color}`} />
                <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground">{t.label}</div>
                <div className="font-heading text-4xl font-extrabold tracking-tight mt-2">{t.credits.toLocaleString()}</div>
                <div className="text-[11px] text-muted-foreground mt-1">credits / month</div>
              </div>
            ))}
          </div>

          {/* Team plans */}
          <div className="pro-card p-5 mb-8" data-testid="team-plans-section">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Team plans — pooled seats &amp; credits</div>
                <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
                  Every Enterprise subscription includes team seats. Add members from your
                  admin console — each seat gets full access, and AI credits are pooled
                  across the whole team so heavy users don&rsquo;t starve.
                </p>
              </div>
              <span className="pill-badge shrink-0"><Sparkles className="w-3 h-3" /> Enterprise</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 text-sm">
              {[
                { seats: '5 seats',   credits: '1,000',  name: 'Team Starter', hint: 'Small shop / R&D pod' },
                { seats: '10 seats',  credits: '2,500',  name: 'Team Growth',  hint: 'Growing operation' },
                { seats: '25 seats',  credits: '5,000',  name: 'Team Plus',    hint: 'Multi-site company' },
                { seats: '26+ seats', credits: 'Custom', name: 'Organizational', hint: 'Talk to sales' },
              ].map((t) => (
                <div key={t.name} className="rounded-md border border-border p-3" data-testid={`team-plan-${t.name.toLowerCase().replace(/ /g, '-')}`}>
                  <div className="font-semibold text-sm">{t.name}</div>
                  <div className="text-xs text-muted-foreground">{t.seats}</div>
                  <div className="mt-1 text-lg font-heading font-bold">{t.credits}</div>
                  <div className="text-[10px] text-muted-foreground">pooled credits / month</div>
                  <div className="text-[10px] text-muted-foreground mt-1 italic">{t.hint}</div>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-muted-foreground mt-3 leading-relaxed">
              Seats are managed from the Enterprise admin console. Invite by email, assign
              instructor / member roles, and revoke access anytime. Unused seats can be
              reassigned mid-cycle.
            </p>
          </div>

          {/* Cost table */}
          <div className="pro-card p-5">
            <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3">
              What each tool costs
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
              {[
                ['Basic freeze-drying question',      '1 credit'],
                ['Course clarification',              '1 credit'],
                ['Definition or terminology',         '1 credit'],
                ['Basic calculation explanation',     '2 credits'],
                ['General process comparison',        '2-3 credits'],
                ['Recipe review',                     '3 credits'],
                ['Basic troubleshooting',             '3-5 credits'],
                ['Batch-performance review',          '5 credits'],
                ['SOP generation',                    '5-10 credits'],
                ['Detailed recipe development',       '5-10 credits'],
                ['Product costing analysis',          '5-10 credits'],
                ['Advanced troubleshooting report',   '10 credits'],
                ['Business-plan section',             '10-20 credits'],
                ['Advanced cycle analysis',           '10-20 credits'],
                ['Regulatory or technical analysis',  '10-25 credits'],
                ['Full enterprise report',            '20-30 credits'],
              ].map(([tool, cost]) => (
                <div
                  key={tool}
                  className="flex items-center justify-between gap-3 py-1 border-b border-border/40 last:border-0"
                >
                  <span className="text-foreground/85 leading-snug">{tool}</span>
                  <span className="font-mono text-xs font-semibold text-primary shrink-0">{cost}</span>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-muted-foreground mt-4 leading-relaxed">
              💡 Admins &amp; moderators don&apos;t consume credits when building courses — that&apos;s on the platform.
              Team plans pool credits across all seats so heavy users don&apos;t starve.
            </p>
          </div>
        </div>

        {/* Trust strip */}
        <div className="mt-14 grid sm:grid-cols-3 gap-3 max-w-4xl mx-auto text-sm">
          <TrustItem icon={ShieldCheck} title="Cancel anytime" desc="No lock-in, no fine print." />
          <TrustItem icon={Sparkles}    title="Vetted tools"    desc="Every calculator is battle-tested." />
          <TrustItem icon={Info}        title="Real support"    desc="You're talking to operators, not a bot." />
        </div>

        {/* FAQ mini */}
        <div className="mt-14 max-w-3xl mx-auto">
          <h2 className="font-heading text-2xl font-bold tracking-tight mb-4">Common questions</h2>
          <div className="space-y-3">
            <FaqRow q="What's included in the Free tier?">
              The freeze-dry product database, the basic household calculator, community forum
              reading and the vendor directory. It&apos;s a real starting point — not a stripped-down demo.
            </FaqRow>
            <FaqRow q="How do AI credits work?">
              Every AI-powered tool costs a small number of credits. Your monthly allowance is granted
              on the 1st of each month and doesn&apos;t roll over. A basic question is 1 credit; deeper
              tools like SOP generation or business plans cost more. Admins can grant bonus credits.
            </FaqRow>
            <FaqRow q="Can I switch tiers later?">
              Yes. Upgrade or downgrade any time from your account portal. Annual plans prorate.
            </FaqRow>
            <FaqRow q="Do you offer refunds?">
              Yes — if a plan isn&apos;t right for your operation, email us within the first 14 days for
              a full refund. We&apos;d rather you stay because it&apos;s useful.
            </FaqRow>
            <FaqRow q="Is the app installable?">
              Yes. Freeze Dry Academy is a Progressive Web App — install it on your phone, tablet or
              desktop from the browser prompt. Basic calculators and stored batches keep working
              when your connection drops; AI and live data still need a network.
            </FaqRow>
          </div>
        </div>
      </main>
    </div>
  );
};

const RedeemSuccessModal = ({ result, tiers, onSaveCard, onDismiss, saveCardLoading }) => {
  const role = (result.tier || '').toLowerCase();
  const roleLabel = (result.tier || '').toUpperCase();
  const tier = tiers.find((t) => (t.name || '').toLowerCase() === role) || tiers[0];
  const RENEWAL_PCT = 20;
  const annualBase = Number(tier?.annual_price || 0);
  const annualDiscounted = annualBase * (1 - RENEWAL_PCT / 100);
  const expDate = result.expires_at
    ? new Date(result.expires_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
    : `${result.days} days from now`;

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onDismiss}
      data-testid="redeem-success-modal"
    >
      <div
        className="bg-background rounded-2xl shadow-2xl w-full max-w-lg p-6 relative animate-in fade-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onDismiss}
          className="absolute top-3 right-3 rounded-full p-1 hover:bg-muted transition-colors"
          data-testid="redeem-success-close"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Celebration hero */}
        <div className="flex flex-col items-center text-center pt-2">
          <div className="h-14 w-14 rounded-full bg-emerald-500 text-white flex items-center justify-center mb-3 shadow-lg shadow-emerald-500/30">
            <CheckCircle2 className="w-8 h-8" />
          </div>
          <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-600">Access unlocked</div>
          <h2 className="font-heading text-3xl font-black mt-1">You're on {roleLabel}.</h2>
          <p className="text-sm text-muted-foreground mt-2 max-w-sm">
            Your <strong className="text-foreground">{result.product_name || `${roleLabel} · ${result.days} days`}</strong> pass is active
            through <strong className="text-foreground">{expDate}</strong>. Explore every feature — courses, AI tools, community, the full deal.
          </p>
        </div>

        {/* Upsell */}
        {tier && annualBase > 0 && (
          <div className="mt-5 rounded-xl border-2 border-primary/30 bg-primary/5 p-4">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-primary" />
              <div className="text-[10px] uppercase tracking-widest font-bold text-primary">Save your seat</div>
            </div>
            <div className="font-heading text-lg font-black mt-1 leading-tight">
              Lock in the whole year with {RENEWAL_PCT}% off — before your trial ends.
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              We won't charge anything today. Save a card with <strong>FREETRIAL20</strong> and we'll only
              bill when your {result.days}-day pass runs out — at{' '}
              <span className="line-through">${annualBase.toFixed(2)}</span>{' '}
              <span className="font-bold text-emerald-700">${annualDiscounted.toFixed(2)}/yr</span>. Cancel anytime.
            </div>
            <div className="flex flex-wrap gap-2 mt-4">
              <Button
                onClick={onSaveCard}
                disabled={saveCardLoading}
                className="flex-1 h-11 rounded-full font-bold"
                data-testid="redeem-save-card-btn"
              >
                {saveCardLoading ? 'Redirecting…' : `Save my card · lock in ${RENEWAL_PCT}% off`}
                {!saveCardLoading && <ArrowRight className="w-4 h-4 ml-1.5" />}
              </Button>
            </div>
          </div>
        )}

        <div className="flex gap-2 mt-4">
          <Button
            onClick={onDismiss}
            variant="outline"
            className="flex-1 h-11 rounded-full font-bold"
            data-testid="redeem-dismiss-btn"
          >
            I'll decide later — take me to my dashboard
          </Button>
        </div>
        <p className="text-[10px] text-muted-foreground text-center mt-3">
          You'll get an in-app reminder as your trial nears the end. You can add a card any time from the Dashboard banner.
        </p>
      </div>
    </div>
  );
};

const ReviewCheckoutModal = ({
  tier, billing, currencyLabel, toDisplay, basePrice, yearlyTotal,
  influencerCode, setInfluencerCode, influencerPreview, clearInfluencer,
  accessCode, setAccessCode, onRedeemAccessCode, redeemingCode,
  onClose, onContinue, continuing,
}) => {
  const pct = influencerPreview?.valid ? Number(influencerPreview.discount_pct || 0) : 0;
  const discounted = pct > 0 ? basePrice * (1 - pct / 100) : basePrice;
  const yearlyDiscounted = pct > 0 ? yearlyTotal * (1 - pct / 100) : yearlyTotal;
  const savings = pct > 0 ? (basePrice - discounted) : 0;

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="review-checkout-modal"
    >
      <div
        className="bg-background rounded-2xl shadow-2xl w-full max-w-lg p-6 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 rounded-full p-1 hover:bg-muted transition-colors"
          data-testid="review-modal-close"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="text-[10px] uppercase tracking-widest font-bold text-primary">Review & apply code</div>
        <h2 className="font-heading text-2xl font-extrabold mt-1">
          {tier.display_name} <span className="text-muted-foreground font-normal">· {billing}</span>
        </h2>

        <div className="mt-4 space-y-3 rounded-lg border border-border p-4 bg-muted/30">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Regular price</span>
            <span className={pct > 0 ? 'line-through text-muted-foreground' : 'font-bold'}>
              ${toDisplay(basePrice).toFixed(2)} / month {currencyLabel}
            </span>
          </div>
          {pct > 0 && (
            <>
              <div className="flex items-center justify-between text-sm text-emerald-700">
                <span className="inline-flex items-center gap-1">
                  <Tag className="w-3 h-3" /> {influencerPreview.code} · {pct}% off
                </span>
                <span className="font-bold">-${toDisplay(savings).toFixed(2)} / month</span>
              </div>
              <div className="flex items-center justify-between text-base pt-2 border-t border-border">
                <span className="font-bold">You'll pay</span>
                <span className="font-heading text-xl font-black text-primary" data-testid="review-final-price">
                  ${toDisplay(discounted).toFixed(2)} / month
                </span>
              </div>
            </>
          )}
          {billing === 'annual' && (
            <div className="text-[11px] text-muted-foreground pt-1">
              Billed today: <strong className="text-foreground">${toDisplay(yearlyDiscounted).toFixed(2)} {currencyLabel}</strong>
              {pct > 0 && <span className="ml-1">(saves ${toDisplay(yearlyTotal - yearlyDiscounted).toFixed(2)})</span>}
            </div>
          )}
        </div>

        {/* Influencer / promo code */}
        <div className="mt-4">
          <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">
            Promo / creator code
          </label>
          <div className="flex gap-2 mt-1.5">
            <Input
              value={influencerCode}
              onChange={(e) => setInfluencerCode(e.target.value.toUpperCase())}
              placeholder="LAUNCH25"
              className="h-10 font-mono uppercase tracking-widest"
              data-testid="review-influencer-input"
            />
            {influencerCode && (
              <Button
                size="sm"
                variant="outline"
                onClick={clearInfluencer}
                className="h-10 rounded-full px-3"
                data-testid="review-clear-influencer"
              >
                Clear
              </Button>
            )}
          </div>
          {influencerPreview && influencerCode && (
            influencerPreview.valid ? (
              <div className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 text-emerald-800 px-2.5 py-1 text-[11px] font-bold mt-2" data-testid="review-promo-valid">
                <CheckCircle2 className="w-3 h-3" /> {pct > 0 ? `${pct}% off applied` : 'Code recognised'}
              </div>
            ) : (
              <div className="inline-flex items-center gap-1.5 rounded-full bg-red-100 text-red-800 px-2.5 py-1 text-[11px] font-bold mt-2" data-testid="review-promo-invalid">
                Code not found or inactive
              </div>
            )
          )}
        </div>

        {/* Access code (bypasses Stripe) */}
        <div className="mt-4 pt-4 border-t border-border">
          <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">
            Access code · skip payment, unlock instantly
          </label>
          <div className="flex gap-2 mt-1.5">
            <Input
              value={accessCode}
              onChange={(e) => setAccessCode(e.target.value.toUpperCase())}
              placeholder="XX-XXXX-XXXX-XXXX"
              className="h-10 font-mono uppercase tracking-widest"
              data-testid="review-access-input"
            />
            <Button
              size="sm"
              onClick={onRedeemAccessCode}
              disabled={redeemingCode || !accessCode.trim()}
              className="h-10 rounded-full font-bold px-4"
              data-testid="review-redeem-access"
            >
              {redeemingCode ? 'Redeeming…' : 'Redeem'}
            </Button>
          </div>
        </div>

        <div className="mt-6 flex gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            className="flex-1 h-11 rounded-full font-bold"
            data-testid="review-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={onContinue}
            disabled={continuing}
            className="flex-1 h-11 rounded-full font-bold"
            data-testid="review-continue-btn"
          >
            {continuing ? 'Redirecting…' : 'Continue to Stripe'}
            {!continuing && <ArrowRight className="w-4 h-4 ml-1" />}
          </Button>
        </div>
        <p className="text-[10px] text-muted-foreground text-center mt-2">
          Payments are processed securely by Stripe. Codes cannot be applied on Stripe's page — please
          apply them here before continuing.
        </p>
      </div>
    </div>
  );
};

const TierCard = ({
  tier, priceOverride, originalPrice, discountPct, billing, yearlyTotal, currencyLabel = 'USD', savings, popular, current, onSubscribe, loading, ctaLabel, disabled,
}) => {
  const displayPrice = priceOverride;
  const featuresList = tier.features || [];
  const isPopular = !!popular;

  return (
    <div
      data-testid={`tier-card-${tier.name || 'free'}`}
      className={`pro-card p-7 flex flex-col relative ${
        isPopular ? 'ring-2 ring-primary ring-offset-2 ring-offset-background' : ''
      }`}
    >
      {isPopular && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <span className="pill-badge">
            <Sparkles className="w-3 h-3" /> Most popular
          </span>
        </div>
      )}
      {originalPrice && discountPct > 0 && (
        <div className="absolute -top-3 right-4">
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500 text-white text-[10px] font-bold px-2.5 py-1 uppercase tracking-widest" data-testid={`tier-discount-${tier.name}`}>
            <Tag className="w-3 h-3" /> {discountPct}% off
          </span>
        </div>
      )}

      <div className="flex items-baseline justify-between">
        <span className="eyebrow">Tier</span>
        {current && (
          <span className="text-[10px] font-bold uppercase tracking-widest text-primary">Current</span>
        )}
      </div>
      <h3 className="font-heading text-2xl font-extrabold tracking-tight mt-1.5">
        {tier.display_name}
      </h3>
      {tier.description && (
        <p className="text-sm text-muted-foreground mt-1 min-h-[40px]">{tier.description}</p>
      )}

      <div className="mt-5">
        {displayPrice === 0 ? (
          <>
            <div className="font-heading text-5xl font-extrabold tracking-tight">$0</div>
            <div className="text-xs text-muted-foreground mt-1">Free forever</div>
          </>
        ) : (
          <>
            <div className="flex items-baseline gap-1.5">
              <span className="font-heading text-5xl font-extrabold tracking-tight">
                ${Math.round(displayPrice)}
              </span>
              <span className="text-muted-foreground text-sm">/ month {currencyLabel}</span>
            </div>
            {originalPrice && (
              <div className="text-xs text-muted-foreground mt-1">
                <span className="line-through">${Math.round(originalPrice)}</span>
                <span className="ml-2 text-emerald-600 font-semibold">code applied</span>
              </div>
            )}
            {billing === 'annual' && yearlyTotal && (
              <div className="text-xs text-muted-foreground mt-1">
                Billed ${yearlyTotal.toFixed(2)} {currencyLabel} annually
                {savings && <span className="ml-1 text-primary font-semibold">· save {savings}%</span>}
              </div>
            )}
            {billing === 'monthly' && !originalPrice && (
              <div className="text-xs text-muted-foreground mt-1">Billed monthly</div>
            )}
          </>
        )}
      </div>

      <ul className="mt-6 space-y-2.5 flex-1">
        {featuresList.map((f) => (
          <li key={f} className="flex items-start gap-2.5 text-sm">
            <span className="h-5 w-5 rounded-full bg-primary/10 text-primary flex items-center justify-center flex-shrink-0 mt-0.5">
              <Check className="w-3 h-3" strokeWidth={3} />
            </span>
            <span className="text-foreground/85 leading-snug">{f}</span>
          </li>
        ))}
      </ul>

      <Button
        onClick={onSubscribe}
        disabled={disabled || loading}
        size="lg"
        className={`mt-7 h-11 rounded-full font-bold ${
          isPopular
            ? 'bg-primary text-primary-foreground hover:bg-primary/90'
            : 'bg-foreground text-background hover:bg-foreground/90'
        }`}
        data-testid={`tier-cta-${tier.name || 'free'}`}
      >
        {loading ? 'Please wait…' : ctaLabel}
        {!loading && !disabled && <ArrowRight className="ml-2 w-4 h-4" />}
      </Button>
    </div>
  );
};

const TrustItem = ({ icon: Icon, title, desc }) => (
  <div className="pro-card p-4 flex items-start gap-3">
    <div className="h-9 w-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
      <Icon className="w-4 h-4" />
    </div>
    <div>
      <div className="font-semibold text-sm">{title}</div>
      <div className="text-xs text-muted-foreground">{desc}</div>
    </div>
  </div>
);

const FaqRow = ({ q, children }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="pro-card">
      <button
        onClick={() => setOpen(!open)}
        className="w-full text-left p-5 flex items-center justify-between gap-3"
        data-testid="faq-toggle"
      >
        <span className="font-semibold text-sm">{q}</span>
        <span className={`text-muted-foreground transition-transform ${open ? 'rotate-45' : ''}`}>+</span>
      </button>
      {open && (
        <div className="px-5 pb-5 text-sm text-muted-foreground leading-relaxed -mt-1">
          {children}
        </div>
      )}
    </div>
  );
};

export default Pricing;
